import {
  Button,
  createStyles,
  Grid,
  Paper,
  TextField,
  Typography,
  WithStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import React from "react";
import type { CauseOfDeath } from "shared/types";
import type { CauseOfDeathFormState } from "../causeOfDeath.types";
import { useCauseOfDeathForm } from "../hooks/useCauseOfDeathForm";

type CauseOfDeathFormProps = {
  causesOfDeath: CauseOfDeath[];
  onCancel: () => void;
  onSubmit: (formState: CauseOfDeathFormState) => void;
  selectedCauseOfDeath: CauseOfDeath | null;
} & WithStyles<typeof styles>;

function CauseOfDeathForm({
  causesOfDeath,
  classes,
  onCancel,
  onSubmit,
  selectedCauseOfDeath,
}: CauseOfDeathFormProps) {
  const { t } = useTranslations();

  const {
    canSubmitForm,
    formState,
    handleChangeName,
    handleResetForm,
    handleSubmitForm,
  } = useCauseOfDeathForm({ causesOfDeath, onSubmit, selectedCauseOfDeath });

  const handleCancelClick = () => {
    handleResetForm();
    onCancel();
  };

  const isEditing = selectedCauseOfDeath != null;
  const headerText = isEditing
    ? t("livestock:edit-cause")
    : t("livestock:new-cause");

  return (
    <Paper className={classes.paper} square={true}>
      <Grid item sm={12} xs={12}>
        <Typography className={classes.formHeader} gutterBottom={true}>
          {headerText}
        </Typography>
      </Grid>
      <Grid item sm={12} xs={12}>
        <Typography className={classes.subHeader} gutterBottom={true}>
          {t("common:general-information")}
        </Typography>
      </Grid>
      <Grid item sm={12} xs={12}>
        <TextField
          autoFocus={true}
          data-testid="cause-of-death-name-input"
          error={formState.name.error != null}
          helperText={formState.name.error}
          InputLabelProps={{ className: classes.inputLabel }}
          label={t("livestock:cause-of-death-short")}
          onChange={handleChangeName}
          required={true}
          value={formState.name.value}
        />
      </Grid>
      <Grid item sm={12} xs={12} className={classes.formActionsContainer}>
        <FormActions
          canSubmit={canSubmitForm}
          isEditing={isEditing}
          onCancel={handleCancelClick}
          onSubmit={handleSubmitForm}
        />
      </Grid>
    </Paper>
  );
}

type FormActionsProps = {
  canSubmit: boolean;
  isEditing: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

function FormActions({
  canSubmit,
  isEditing,
  onCancel,
  onSubmit,
}: FormActionsProps) {
  const { t } = useTranslations();

  const submitButtonText = isEditing
    ? t("common:actions.save")
    : t("common:actions.add");

  return (
    <>
      <Button color="primary" data-testid="cancel-button" onClick={onCancel}>
        {t("common:actions.cancel")}
      </Button>
      <Button
        color="primary"
        data-testid="submit-button"
        disabled={!canSubmit}
        onClick={onSubmit}
        variant="contained"
      >
        {submitButtonText}
      </Button>
    </>
  );
}

const styles = () =>
  createStyles({
    paper: {
      borderRadius: "16px",
      marginTop: "10px",
      padding: "20px",
      width: "100%",
    },
    formHeader: {
      color: "#333333",
      fontSize: "20px",
      fontStretch: "medium",
      fontStyle: "medium",
      fontWeight: "bold",
      letterSpacing: "medium",
      lineHeight: "medium",
      marginTop: "16px",
    },
    subHeader: {
      fontFamily: "Roboto",
      fontSize: "14px",
      letterSpacing: "-0.03px",
      color: "#535353",
      fontWeight: "bold",
      marginTop: "16px",
      marginBottom: "12px",
    },
    inputLabel: {
      width: "max-content",
    },
    formActionsContainer: {
      marginTop: "36px",
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: "20px",
      width: "100%",
    },
  });

export default withStyles(styles)(CauseOfDeathForm);
